import api from './api';
import { useAuthStore } from '@/store/auth';
import type {
  QuoteRequestPayload,
  QuoteSubmitPayload,
  QuoteSubmitResponse,
  ProposalPayload,
  ProposalResponse,
  PaymentInitiation,
  PaymentResponse,
  IntegrationPolicyStatus,
  PreviousInsurer,
  PaginatedResponse,
  EgressMode,
  InsurerEgressInfo,
} from '@/types';

export interface BatchQuoteUploadResponse {
  batch_id?: string;
  id: string;
  [key: string]: unknown;
}

export interface BatchQuoteStatus {
  id: string;
  batch_id?: string;
  status: string;
  total_items: number;
  completed_items: number;
  failed_items: number;
  created_at: string;
  [key: string]: unknown;
}

export interface APICallLog {
  id: string;
  insurer: string;
  insurer_code: string;
  insurer_name: string;
  broker: string | null;
  broker_code: string | null;
  broker_name: string | null;
  adapter_version: string;
  endpoint_name: string;
  environment: string;
  http_method: string;
  request_url: string;
  response_status_code: number | null;
  latency_ms: number | null;
  status: string;
  error_message: string | null;
  platform_error_code: string | null;
  started_at: string;
  completed_at: string | null;
  retry_attempt: number;
  parent_call: string | null;
  created_at: string;
}

export interface CircuitBreakerConfig {
  id: string;
  insurer: string;
  insurer_code: string;
  insurer_name: string;
  environment: string;
  endpoint_name: string | null;
  failure_threshold: number;
  failure_window_secs: number;
  cooldown_secs: number;
  is_enabled: boolean;
  current_state: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPaginatedResponse<T> {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface AdminAPILogsParams {
  page?: number;
  insurer?: string;
  broker?: string;
  status?: string;
  environment?: string;
  endpoint_name?: string;
  started_at__gte?: string;
  started_at__lte?: string;
  search?: string;
  ordering?: string;
}

export interface CircuitBreakerResetResult {
  insurer_code: string;
  reset_count: number;
  message: string;
}

/** Backend environments. Note these are 'sandbox'/'production' — the UI used
 *  to say 'uat', which no backend route ever accepted. */
export type IntegrationEnv = 'sandbox' | 'production';

/**
 * `awaiting_activation` — adapter exists, but no country admin has enabled this
 * insurer for this environment yet. `no_adapter` — the platform has no adapter
 * for this insurer at all, so no admin action helps.
 */
export type CredentialStatus =
  | 'connected'
  | 'error'
  | 'untested'
  | 'not_configured'
  | 'awaiting_activation'
  | 'no_adapter';

/** One field of an insurer's credential form, declared by its plug-in on the server. Labels only -- never a value. */
export interface CredentialFieldSchema {
  key: string;
  label: string;
  help: string;
  secret: boolean;
  required: boolean;
  /** The credential column or `additional_params.<key>` it is stored in. */
  maps_to: string;
}

/** What the server holds for one field. A secret's value never comes back: only `stored`
 *  (and the masked `hint` for an API key). */
export interface StoredCredentialField {
  stored: boolean;
  value?: string;
  hint?: string;
}

export interface InsurerCredentialEnv {
  base_url: string;
  api_key_masked: string;
  insurer_account_id: string;
  is_configured: boolean;
  is_enabled: boolean;
  status: CredentialStatus;
  /** This broker already has an open activation request for this environment. */
  activation_requested?: boolean;
  last_tested_at: string | null;
  last_test_message: string;
  /** Whether a usable API key / Password is STORED. The values never come
   *  back, so these are the only way the page can say a save took. */
  has_api_key: boolean;
  has_client_secret: boolean;
  /** When the credential was last saved (not tested); null when never. */
  saved_at: string | null;
  /** Per declared field: what the server holds (see StoredCredentialField). */
  stored_fields?: Record<string, StoredCredentialField>;
}

export interface InsurerCredentialRow {
  insurer_id: string;
  insurer_code: string;
  insurer_name: string;
  /** U-05: `Insurer.effective_logo_url` (was the legacy pasted url, empty on
   *  every live insurer). '' when the insurer has no picture. */
  logo_url: string;
  /** U-05: the insurer's short blurb; '' / absent when it has none. */
  insurer_description?: string;
  sandbox: InsurerCredentialEnv;
  production: InsurerCredentialEnv;
  /** The insurer plug-in's credential form; [] / absent when the insurer has none. */
  credential_fields?: CredentialFieldSchema[];
}

export interface ActivationRequest {
  id: string;
  broker_id: string;
  broker_name: string;
  insurer_id: string;
  insurer_code: string;
  insurer_name: string;
  environment: IntegrationEnv;
  status: 'pending' | 'approved' | 'declined';
  note: string;
  created_at: string | null;
}

export interface InsurerCredentialsResponse {
  count: number;
  results: InsurerCredentialRow[];
}

export interface TestConnectionResult {
  insurer_id: string;
  insurer_code: string;
  environment: IntegrationEnv;
  success: boolean;
  /** Which leg the result came from — see `KnownTestConnectionStage`. Typed
   *  `string` on purpose: an unfamiliar stage is shown verbatim rather than
   *  mapped onto a familiar one. */
  stage: string;
  message: string;
  http_status?: number;
}

export interface QuoteStatusQuote {
  id: string;
  quote_number: string;
  insurer_code: string;
  insurer_name: string;
  insurer_quote_id: string;
  gross_premium: string;
  net_premium: string;
  total_premium: string;
  od_premium: string;
  tp_premium: string;
  gst_amount: string;
  status: string;
  error_message: string;
  valid_until: string | null;
}

export interface QuoteStatusResponse {
  quote_request_id: string;
  request_number: string;
  status: string;
  quotes_count: number;
  quotes: QuoteStatusQuote[];
}

/** The country admin's per-insurer integration switch.
 *
 *  Extends `InsurerEgressInfo`: the API may report HOW insurer traffic leaves
 *  this server (`egress_mode`, `relay_host`). Both are optional — an older
 *  backend build omits them, and that absence renders as "unknown", never as
 *  "direct". No relay URL or key is ever part of this payload. */
export interface IntegrationConfig extends InsurerEgressInfo {
  id: string;
  insurer: string;
  insurer_code: string;
  insurer_name: string;
  environment: IntegrationEnv;
  active_api_version: string;
  /** Insurer API endpoint. Country-admin owned; brokers inherit it read-only. */
  base_url: string;
  /** Hosts for services the insurer serves apart from base_url, e.g.
   *  { payment: 'https://…', pdf: 'https://…' }. Empty for single-host insurers. */
  service_endpoints: Record<string, string>;
  is_enabled: boolean;
  available_versions: string[];
  adapter_registered: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminBrokerCredentialStatus {
  id: string;
  broker_id: string;
  broker_name: string;
  insurer_id: string;
  insurer_code: string;
  insurer_name: string;
  environment: IntegrationEnv;
  is_configured: boolean;
  is_enabled: boolean;
  last_tested_at: string | null;
  last_test_ok: boolean | null;
}

export interface AdminTestConnectionResult {
  insurer_code: string;
  environment: string;
  status: string;
  /** Which leg the result came from — see `KnownTestConnectionStage`. Optional
   *  because a build that predates staged results does not send it; absent
   *  renders as "not reported", never as a guess. */
  stage?: string;
  /** The server's own words about what happened. */
  message?: string;
  http_status?: number;
  adapter?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Reading a Test Connection result, and reading the egress route
//
// Both are pure functions over what the API actually sent. They exist here,
// next to the wire shapes, because the two screens that render them (country
// admin Integrations, broker API Credentials) must say the SAME thing about
// the same payload — and because neither may invent a value the server did
// not send.
// ---------------------------------------------------------------------------

/** One route insurer traffic was reported to take. */
export interface EgressRoute {
  mode: 'direct' | 'relay';
  /** Host only, and only when the server reported one. */
  host: string | null;
}

/**
 * What the whole set of integration configs says about egress.
 *
 * `unknown` covers both "this API build does not report egress" and "there is
 * nothing configured to report on". It is deliberately NOT collapsed into
 * `direct`: assuming direct is how a relay outage stays invisible.
 */
export type EgressSummary =
  | { mode: 'direct' }
  | { mode: 'relay'; host: string | null }
  | { mode: 'unknown' }
  | { mode: 'mixed'; routes: EgressRoute[] };

export function summariseEgress(
  configs: ReadonlyArray<InsurerEgressInfo> | undefined | null,
): EgressSummary {
  const seen = new Map<string, EgressRoute>();

  for (const config of configs ?? []) {
    const mode: EgressMode | undefined = config?.egress_mode;
    // Anything that is not an explicit 'direct'/'relay' — absent, 'unknown',
    // or a value this build has never heard of — contributes nothing.
    if (mode !== 'direct' && mode !== 'relay') continue;
    const host = mode === 'relay' ? (config.relay_host ?? '').trim() || null : null;
    seen.set(`${mode}:${host ?? ''}`, { mode, host });
  }

  const routes = [...seen.values()];
  if (routes.length === 0) return { mode: 'unknown' };
  if (routes.length === 1) {
    const only = routes[0];
    return only.mode === 'relay' ? { mode: 'relay', host: only.host } : { mode: 'direct' };
  }
  return { mode: 'mixed', routes };
}

/** A Test Connection stage, in words an operator can act on. */
export interface TestStageDescription {
  /** Exactly what the server called it, or 'not reported' when it said nothing. */
  stage: string;
  /** False when the server sent a stage this build does not recognise (or none). */
  known: boolean;
  /** What happened, in one line. */
  label: string;
  /** How far the request actually got. This is the part that gets conflated. */
  meaning: string;
  /** What to do about it. Empty when we cannot honestly say. */
  nextStep: string;
}

const NOT_REPORTED = 'not reported';

/**
 * The backend's stage vocabulary (apps/integrations/views_credentials.py).
 *
 * The whole point of this table is that `network` and `auth` can never read
 * alike: one means the insurer never answered, the other means it answered and
 * refused the credential. Diagnosing them is completely different work.
 */
const TEST_STAGE_DESCRIPTIONS: Record<string, Omit<TestStageDescription, 'stage' | 'known'>> = {
  integration: {
    label: 'Integration not enabled',
    meaning: 'No call was attempted. This insurer is not switched on for this environment.',
    nextStep: 'Enable it under Insurer Integrations, endpoint included.',
  },
  adapter: {
    label: 'No adapter for this version',
    meaning:
      'No call was attempted. The platform has no adapter registered for the configured API version.',
    nextStep: 'Pick a version that has an adapter, or have one built.',
  },
  endpoint: {
    label: 'No API endpoint set',
    meaning: 'No call was attempted. There is no URL to call for this insurer and environment.',
    nextStep: 'Set the endpoint under Insurer Integrations.',
  },
  credentials: {
    label: 'Credential missing or unusable',
    meaning:
      'Nothing was sent to the insurer — the credential this platform holds is missing or could not be used.',
    nextStep: 'Enter the credential under Broker → API Credentials, save, then test again.',
  },
  authenticate: {
    label: 'Could not build an authenticated session',
    meaning:
      'The request never left this server: the adapter failed while assembling the credential into an auth header.',
    nextStep: 'Check that each value is in its own field — the webUserId, the password and the API key are not interchangeable.',
  },
  network: {
    label: 'Could not reach the insurer',
    meaning:
      'The request left this server and nothing usable came back — DNS, routing, a firewall or a timeout. Nothing was judged about the credential.',
    nextStep:
      'Check the endpoint host and the egress route above: an insurer that filters by geography will not answer a call that leaves from the wrong country.',
  },
  auth: {
    label: 'Reached the insurer — credential rejected',
    meaning:
      'The insurer answered, so the network path works. It refused the credential that was sent.',
    nextStep:
      'Check the values and the environment they belong to — a sandbox credential is rejected in production, and a webUserId in the API key field builds the wrong auth header.',
  },
  reachable: {
    label: 'Reached the insurer',
    meaning: 'The insurer answered and did not reject the credential.',
    nextStep: 'A live quote is the only proof the credential works end to end.',
  },
  ok: {
    label: 'Reached the insurer',
    meaning: 'The insurer answered and did not reject the credential.',
    nextStep: 'A live quote is the only proof the credential works end to end.',
  },
};

/**
 * Describe a stage without ever inventing one.
 *
 * An unrecognised stage is reported verbatim with no interpretation attached,
 * and a missing stage says so. Guessing here would be worse than useless: the
 * whole value of this panel is that it can be trusted about where the call
 * stopped.
 */
export function describeTestStage(stage: string | null | undefined): TestStageDescription {
  const raw = (stage ?? '').trim();

  if (!raw) {
    return {
      stage: NOT_REPORTED,
      known: false,
      label: 'Result without a stage',
      meaning:
        'The server did not say which leg of the call this result came from, so how far the request got cannot be told from here.',
      nextStep: '',
    };
  }

  const described = TEST_STAGE_DESCRIPTIONS[raw];
  if (described) return { stage: raw, known: true, ...described };

  return {
    stage: raw,
    known: false,
    label: 'Unrecognised stage',
    meaning:
      'This screen does not know this stage, so it will not say how far the request got. The server\'s own message is below, unaltered.',
    nextStep: '',
  };
}

export const integrationsService = {
  // Quote operations.
  // Accepts either the legacy QuoteRequestPayload shape (the motor-only
  // QuoteRequestPage that sent it was retired in Task 2.8; the union is kept
  // for any other legacy callers) or the real QuoteSubmitSerializer contract
  // (QuoteSubmitPayload) used by the unified Quote Wizard (Task 2.5+). Both
  // post to the same endpoint; the response shape below reflects what
  // QuoteSubmitView actually returns today.
  async submitQuoteRequest(
    data: QuoteRequestPayload | QuoteSubmitPayload
  ): Promise<QuoteSubmitResponse> {
    const response = await api.post('/integrations/quotes/', data);
    return response.data;
  },

  // Proposal operations
  async createProposal(data: ProposalPayload): Promise<ProposalResponse> {
    const response = await api.post('/integrations/policies/issue/', data);
    return response.data;
  },

  // Payment
  async initiatePayment(data: PaymentInitiation): Promise<PaymentResponse> {
    const response = await api.post('/integrations/payment/initiate/', data);
    return response.data;
  },

  // Policy status
  async getPolicyStatus(policyNumber: string): Promise<IntegrationPolicyStatus> {
    const response = await api.get(`/integrations/policies/${policyNumber}/status/`);
    return response.data;
  },

  // PDF download
  async downloadPolicyPDF(policyNumber: string): Promise<Blob> {
    const response = await api.get(`/integrations/policies/${policyNumber}/pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Previous insurers
  async getPreviousInsurers(): Promise<PreviousInsurer[]> {
    const response = await api.get('/insurers/insurers/lookup/');
    return response.data;
  },

  // Webhooks (broker self-service)
  async getWebhooks(): Promise<PaginatedResponse<Record<string, unknown>>> {
    const response = await api.get('/integrations/broker/webhooks/');
    return response.data;
  },

  async createWebhook(data: {
    event_type: string;
    callback_url: string;
    secret_key: string;
  }): Promise<Record<string, unknown>> {
    const response = await api.post('/integrations/broker/webhooks/', data);
    return response.data;
  },

  async updateWebhook(id: string, data: Partial<{
    event_type: string;
    callback_url: string;
    secret_key: string;
    is_active: boolean;
  }>): Promise<Record<string, unknown>> {
    const response = await api.patch(`/integrations/broker/webhooks/${id}/`, data);
    return response.data;
  },

  async deleteWebhook(id: string): Promise<void> {
    await api.delete(`/integrations/broker/webhooks/${id}/`);
  },

  // Delivery log, test event and resend (#670)
  async getWebhookDeliveries(id: string): Promise<PaginatedResponse<Record<string, unknown>>> {
    const response = await api.get(`/integrations/broker/webhooks/${id}/deliveries/`);
    return response.data;
  },

  async testWebhook(id: string): Promise<Record<string, unknown>> {
    const response = await api.post(`/integrations/broker/webhooks/${id}/test/`);
    return response.data;
  },

  async resendWebhookDelivery(deliveryId: string): Promise<Record<string, unknown>> {
    const response = await api.post(`/integrations/broker/webhook-deliveries/${deliveryId}/resend/`);
    return response.data;
  },

  async getAPIAccess(): Promise<Record<string, unknown>> {
    const response = await api.get('/integrations/broker/api-access/');
    return response.data;
  },

  async getAPIUsage(): Promise<Record<string, unknown>> {
    const response = await api.get('/integrations/broker/api-usage/');
    return response.data;
  },

  // Broker ↔ insurer credentials. Distinct from getAPIAccess() above, which
  // is the broker's key for calling *this* platform.
  //
  // Every call carries the broker chosen in the header's broker switcher.
  // The server refuses to guess for an admin of several brokers (a guess
  // would store one broker's insurer key against another), and it does not
  // remember the switcher's choice itself -- so without this, such an admin
  // could not open this page at all.
  async getInsurerCredentials(): Promise<InsurerCredentialsResponse> {
    const response = await api.get('/integrations/broker/insurer-credentials/', {
      params: selectedBrokerParam(),
    });
    return response.data;
  },

  async updateCredentials(
    insurerId: string,
    env: IntegrationEnv,
    // The generated form: {field key: value} for the insurer plug-in's declared
    // fields. Blank values are left out -- the server keeps what it holds. No
    // base_url: the endpoint is the country admin's.
    data: { fields: Record<string, string>; is_enabled?: boolean },
  ): Promise<
    InsurerCredentialEnv & { insurer_id: string; insurer_code: string; environment: IntegrationEnv }
  > {
    const response = await api.put('/integrations/broker/insurer-credentials/', {
      insurer_id: insurerId,
      environment: env,
      ...data,
      ...selectedBrokerParam(),
    });
    return response.data;
  },

  async deleteCredentials(insurerId: string, env: IntegrationEnv): Promise<void> {
    await api.delete(`/integrations/broker/insurer-credentials/${insurerId}/${env}/`, {
      params: selectedBrokerParam(),
    });
  },

  /** Ask the country admin to enable this insurer. Idempotent per triple. */
  async requestActivation(insurerId: string, env: IntegrationEnv): Promise<void> {
    await api.post('/integrations/broker/activation-requests/', {
      insurer_id: insurerId,
      environment: env,
      ...selectedBrokerParam(),
    });
  },

  /** The country admin's queue of brokers waiting on an integration. */
  async listActivationRequests(): Promise<{ count: number; results: ActivationRequest[] }> {
    const response = await api.get('/integrations/admin/activation-requests/');
    return response.data;
  },

  async testConnection(insurerId: string, env: IntegrationEnv): Promise<TestConnectionResult> {
    const response = await api.post('/integrations/broker/test-connection/', {
      insurer_id: insurerId,
      environment: env,
      ...selectedBrokerParam(),
    });
    return response.data;
  },

  // Quote polling — the poll_url returned by the submit endpoint.
  async getQuoteStatus(quoteRequestId: string): Promise<QuoteStatusResponse> {
    const response = await api.get(`/integrations/quotes/${quoteRequestId}/`);
    return response.data;
  },

  // Batch quote operations
  async uploadBatchQuote(file: File): Promise<BatchQuoteUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/integrations/quotes/batch/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async getBatchStatus(batchId: string): Promise<BatchQuoteStatus> {
    const response = await api.get(`/integrations/quotes/batch/${batchId}/`);
    return response.data;
  },

  async downloadBatchResults(batchId: string): Promise<Blob> {
    const response = await api.get(`/integrations/quotes/batch/${batchId}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async listBatches(page: number = 1): Promise<PaginatedResponse<BatchQuoteStatus>> {
    const response = await api.get(`/integrations/quotes/batch/?page=${page}`);
    return response.data;
  },

  // Admin endpoints (super_admin + country_admin)
  async adminListApiLogs(
    params: AdminAPILogsParams = {},
  ): Promise<AdminPaginatedResponse<APICallLog>> {
    const response = await api.get('/integrations/admin/api-logs/', { params });
    return response.data;
  },

  async adminListCircuitBreakers(): Promise<AdminPaginatedResponse<CircuitBreakerConfig>> {
    const response = await api.get('/integrations/admin/circuit-breakers/');
    return response.data;
  },

  async adminResetCircuitBreaker(insurerCode: string): Promise<CircuitBreakerResetResult> {
    const response = await api.post(
      `/integrations/admin/circuit-breakers/${insurerCode}/reset/`,
    );
    return response.data;
  },

  async adminTestConnection(
    insurerCode: string,
    environment: IntegrationEnv = 'sandbox',
  ): Promise<AdminTestConnectionResult> {
    const response = await api.post('/integrations/admin/test-connection/', {
      insurer_code: insurerCode,
      environment,
    });
    return response.data;
  },

  // Country admin: enable/disable an insurer integration and pick its adapter
  // version. Previously only reachable through Django admin.
  async adminListIntegrationConfigs(
    environment?: IntegrationEnv,
  ): Promise<AdminPaginatedResponse<IntegrationConfig>> {
    const response = await api.get('/integrations/admin/integration-configs/', {
      params: environment ? { environment } : {},
    });
    return response.data;
  },

  async adminCreateIntegrationConfig(data: {
    insurer: string;
    environment: IntegrationEnv;
    active_api_version: string;
    base_url?: string;
    service_endpoints?: Record<string, string>;
    is_enabled: boolean;
  }): Promise<IntegrationConfig> {
    const response = await api.post('/integrations/admin/integration-configs/', data);
    return response.data;
  },

  async adminUpdateIntegrationConfig(
    id: string,
    data: Partial<Pick<IntegrationConfig, 'active_api_version' | 'is_enabled' | 'base_url' | 'service_endpoints'>>,
  ): Promise<IntegrationConfig> {
    const response = await api.patch(`/integrations/admin/integration-configs/${id}/`, data);
    return response.data;
  },

  async adminDeleteIntegrationConfig(id: string): Promise<void> {
    await api.delete(`/integrations/admin/integration-configs/${id}/`);
  },

  async adminListBrokerCredentialStatus(params: {
    broker?: string;
    insurer?: string;
    environment?: IntegrationEnv;
  } = {}): Promise<AdminPaginatedResponse<AdminBrokerCredentialStatus>> {
    const response = await api.get('/integrations/admin/broker-credentials/', { params });
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Vehicle lookup vendors (country admin)
// ---------------------------------------------------------------------------

/** A vendor the platform buys registration lookups from.
 *  Country-admin only: brokers are billed for a search but never choose a
 *  vendor. Secrets are write-only — reads return a mask or a boolean. */
export interface LookupVendor {
  id: string;
  code: string;
  name: string;
  environment: 'uat' | 'production';
  base_url: string;
  token_url: string;
  vendor_public_key: string;
  priority: number;
  cost_per_lookup: string;
  is_enabled: boolean;
  api_key_masked: string;
  has_api_secret: boolean;
  has_private_key: boolean;
  is_configured: boolean;
}

export type LookupVendorWrite = Partial<
  Pick<
    LookupVendor,
    | 'code' | 'name' | 'environment' | 'base_url' | 'token_url'
    | 'vendor_public_key' | 'priority' | 'cost_per_lookup' | 'is_enabled'
  >
> & { api_key?: string; api_secret?: string; private_key?: string };

export const lookupVendorsService = {
  async list(): Promise<AdminPaginatedResponse<LookupVendor>> {
    const response = await api.get('/vehicles/admin/lookup-vendors/');
    return response.data;
  },

  async create(data: LookupVendorWrite): Promise<LookupVendor> {
    const response = await api.post('/vehicles/admin/lookup-vendors/', data);
    return response.data;
  },

  async update(id: string, data: LookupVendorWrite): Promise<LookupVendor> {
    const response = await api.patch(`/vehicles/admin/lookup-vendors/${id}/`, data);
    return response.data;
  },
};

/** What a registration search costs one broker, and whether they may buy one.
 *
 *  Two owners, split deliberately. The country admin sets the commercial
 *  terms (`is_enabled`, `settlement`, `price_per_search`, `balance`); the
 *  broker owns exposure (`public_lookup_enabled`, `public_daily_cap`) — those
 *  two are read-only on this endpoint and are set by the broker themselves.
 *
 *  Absence is a decision: a broker with no plan cannot buy a search at all. */
export interface BrokerLookupPlan {
  id: string;
  broker: string;
  broker_name: string;
  broker_code: string;
  is_enabled: boolean;
  settlement: 'prepaid' | 'postpaid';
  price_per_search: string;
  balance: string;
  public_lookup_enabled: boolean;
  public_daily_cap: number;
  public_searches_today: number;
}

export type BrokerLookupPlanWrite = Partial<
  Pick<BrokerLookupPlan, 'is_enabled' | 'settlement' | 'price_per_search' | 'balance'>
> & { broker?: string };

export const brokerLookupPlansService = {
  async list(): Promise<AdminPaginatedResponse<BrokerLookupPlan>> {
    const response = await api.get('/vehicles/admin/lookup-plans/');
    return response.data;
  },

  async create(data: BrokerLookupPlanWrite): Promise<BrokerLookupPlan> {
    const response = await api.post('/vehicles/admin/lookup-plans/', data);
    return response.data;
  },

  async update(id: string, data: BrokerLookupPlanWrite): Promise<BrokerLookupPlan> {
    const response = await api.patch(`/vehicles/admin/lookup-plans/${id}/`, data);
    return response.data;
  },
};

/** The broker's own view of their plan: terms visible but fixed, exposure
 *  theirs to set. `has_plan: false` means the country admin has not set terms,
 *  which is why the paid search button should not be offered at all. */
export interface OwnLookupPlan {
  has_plan: boolean;
  is_enabled: boolean;
  settlement?: 'prepaid' | 'postpaid';
  price_per_search?: string;
  balance?: string;
  public_lookup_enabled?: boolean;
  public_daily_cap?: number;
  public_searches_today?: number;
  detail?: string;
}

export const ownLookupPlanService = {
  async get(): Promise<OwnLookupPlan> {
    const response = await api.get('/vehicles/lookup-plan/');
    return response.data;
  },

  /** Only the broker's half is writable; the terms are ignored if sent. */
  async update(
    data: { public_lookup_enabled?: boolean; public_daily_cap?: number },
  ): Promise<OwnLookupPlan> {
    const response = await api.patch('/vehicles/lookup-plan/', data);
    return response.data;
  },
};

/** `{ broker_id }` for the broker picked in the header switcher, or nothing
 * when none is picked (a single-broker admin, whom the server resolves). */
function selectedBrokerParam(): { broker_id?: string } {
  const id = useAuthStore.getState().tenantContext?.id;
  return id ? { broker_id: id } : {};
}
